export type Campaign = {
  readonly id: string; readonly businessAccountId?: string; readonly name: string;
  readonly state: string; readonly scheduledAt: string | null;
  readonly templateId?: string; readonly phoneNumberId?: string; readonly blockedReason?: string | null;
};
export type TemplateOption = {
  readonly id: string; readonly businessAccountId: string; readonly name: string;
  readonly language: string; readonly status: string;
};
export type PhoneOption = {
  readonly id: string; readonly businessAccountId: string; readonly maskedDisplayNumber: string;
  readonly verifiedName: string; readonly status: string;
};
export type Validation = {
  readonly canSchedule: boolean;
  readonly validation: {
    readonly eligible: number; readonly suppressed: number; readonly invalid: number; readonly duplicate: number;
    readonly errors: readonly { readonly code: string; readonly field?: string; readonly contactId?: string }[];
  };
};
export const validationMessages: Readonly<Record<string, string>> = {
  business_account_not_active: 'BM inativa.', business_account_not_connected: 'BM não conectada.',
  template_not_approved: 'Template não aprovado.', phone_not_connected: 'Número não conectado.',
  messaging_limit_unreported: 'Limite não informado pela Meta. Sincronize a BM; nenhum limite será presumido.',
  messaging_limit_stale: 'Limite desatualizado. Sincronize a BM.', messaging_limit_exceeded: 'Público elegível acima do limite confirmado.',
  schedule_in_past: 'O agendamento deve estar no futuro.', audience_empty: 'Nenhum destinatário elegível.',
  missing_template_variable: 'Variável obrigatória do template ausente no contato.',
};
export const stateLabel: Readonly<Record<string, string>> = {
  draft: 'Rascunho', validating: 'Validando', scheduled: 'Agendada', running: 'Em execução',
  paused: 'Pausada', completed: 'Concluída', cancelled: 'Cancelada', blocked: 'Bloqueada', failed: 'Falhou',
};

const wallClock = (date: Date): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const part = (name: string) => parts.find((item) => item.type === name)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
};

export const scheduleInstant = (local: string): string | null => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return null;
  const target = Date.parse(local + ':00Z');
  if (!Number.isFinite(target) || new Date(target).toISOString().slice(0, 16) !== local) return null;
  let instant = target;
  for (let step = 0; step < 3; step += 1) {
    const observed = Date.parse(wallClock(new Date(instant)) + ':00Z');
    instant += target - observed;
  }
  return wallClock(new Date(instant)) === local ? new Date(instant).toISOString() : null;
};

export const validValidation = (value: Validation): boolean => value?.validation !== undefined && value.validation !== null
  && typeof value.canSchedule === 'boolean'
  && ['eligible', 'suppressed', 'invalid', 'duplicate'].every((key) => {
    const count = value.validation[key as 'eligible' | 'suppressed' | 'invalid' | 'duplicate'];
    return Number.isSafeInteger(count) && count >= 0;
  }) && Array.isArray(value.validation.errors);
